import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Button, Tag, Spin, Checkbox } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { QUERY_LANDLORD } from './query';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';
import { debounce, uniqBy } from 'lodash';
import classNames from 'classnames';

export default function LandlordSelector(props) {
  const { onConfirm, value, defaultQueryParams } = props;
  const ButtonRef = useRef(null);
  const { t } = useTranslation();

  const lastTimeSearch = useRef('');

  const [isShowFilterTable, setShowFilterTable] = useState(false);
  const [selectedLandlord, setSelectedLandlord] = useState(value || []);
  const [landlordList, setLandlordList] = useState([]);
  const [selectedLandlordList, setSelectedLandlordList] = useState(
    defaultQueryParams.selectedLandlordList || [],
  );
  const [currentPageNumber, setPageNumber] = useState(defaultQueryParams.pageNumber || 1);
  const [searchLandlord, setSearchLandlord] = useState('');

  const { data: landlordData, loading: getLandlordLoading } = useQuery(QUERY_LANDLORD, {
    variables: {
      nameLike: searchLandlord || null,
      pageNumber: currentPageNumber,
      pageSize: 20,
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (landlordData && !getLandlordLoading) {
      const newestLandlordList = landlordData?.landlords?.edges?.map(item => item.node);
      if (landlordList.length && searchLandlord === lastTimeSearch.current) {
        setLandlordList(uniqBy(landlordList.concat(newestLandlordList), landlord => landlord.id));
      } else {
        setLandlordList(newestLandlordList);
      }

      lastTimeSearch.current = searchLandlord;
    }
  }, [!!landlordData]);

  const onChange = ids => {
    setSelectedLandlord(ids);
    onConfirm({
      ids,
      selectedLandlordList,
      currentPageNumber,
    });
  };

  const selectedLandlordTags = useMemo(() => {
    // Can't just use selectedLandlordList
    // because selectedLandlordList is no confirm result.
    return selectedLandlordList.filter(landlord => selectedLandlord.includes(landlord.id));
  }, [selectedLandlord, selectedLandlordList]);

  const filterTableDataSource = useMemo(() => {
    const withSelectedLandlordNewList = uniqBy(
      selectedLandlordList.concat(landlordList),
      landlord => landlord.id,
    );
    return withSelectedLandlordNewList.map(landlord => ({
      label: landlord?.name,
      value: landlord?.id,
    }));
  }, [landlordList]);

  const handleOnUnselectLandlord = deleteLandlordId => {
    const afterDeleteLandlordIds = selectedLandlord.filter(
      landlord => deleteLandlordId !== landlord,
    );
    setSelectedLandlordList(newestSelectedLandlordLast =>
      newestSelectedLandlordLast.filter(item => item.id !== deleteLandlordId),
    );
    onChange(afterDeleteLandlordIds);
  };

  const handleOnSearch = v => {
    setPageNumber(1);
    setSearchLandlord(v);
  };

  const handleOnSelect = ids => {
    const landlordListWithSelectedLandlord = uniqBy(
      selectedLandlordList.concat(landlordList),
      landlord => landlord.id,
    );
    setSelectedLandlordList(
      landlordListWithSelectedLandlord.filter(landlord => ids.includes(landlord.id)),
    );
  };

  const handleFetchMore = useCallback(() => {
    if (landlordData?.landlords?.pageInfo?.hasNextPage && !getLandlordLoading) {
      setPageNumber(currentPageNumber => currentPageNumber + 1);
    }
  }, [landlordData, getLandlordLoading]);

  return (
    <div className="landlord-selector">
      <h5 className="landlord-selector__title">
        {t('cms.special_offer.map.modal.landlord_selector.title')}:
      </h5>

      <div className="landlord-selector__list">
        <div className="landlord-selector__add-btn">
          <Button ref={ButtonRef} onClick={() => setShowFilterTable(true)}>
            <PlusOutlined />
            {t('cms.special_offer.map.modal.landlord_selector.text')}
          </Button>
          <div
            style={{ position: 'absolute', top: ButtonRef?.current?.buttonNode?.offsetTop || 0 }}
          >
            <ScrollLoadCheckBoxGroup
              loading={getLandlordLoading}
              list={filterTableDataSource}
              value={selectedLandlord}
              visible={isShowFilterTable}
              onClose={setShowFilterTable}
              onChange={onChange}
              onReachEndList={handleFetchMore}
              onSearch={handleOnSearch}
              onSelect={handleOnSelect}
            />
          </div>
        </div>
        {selectedLandlordTags.map(landlord => (
          <Tag
            key={landlord.id}
            className="landlord-selector__list__tag-container"
            visible
            closable
            onClose={() => handleOnUnselectLandlord(landlord.id)}
          >
            <div className="landlord-selector__list__tag">
              {landlord.name}
              <div className="landlord-selector__list__tag__divider" />
              <span className="landlord-selector__list__tag__gray">
                {landlord?.propertiesCount || 0}
                {t(landlord?.propertiesCount > 1 ? ' Properties' : ' Property')}
              </span>
            </div>
          </Tag>
        ))}
      </div>
    </div>
  );
}

const useStyles = makeStyles(
  theme => ({
    filterTable: props => ({
      backgroundColor: theme.colors.gray1,
      borderRadius: theme.borderRadius.min,
      display: 'block',
      position: 'absolute',
      boxShadow: '0px 9px 28px 8px #0000000D',
      top: props.top || '37px',
      zIndex: 3,

      '& .ant-checkbox-group': {
        paddingTop: 0,
        paddingBottom: 0,
      },
    }),
    bottomBox: props => ({
      ...theme.flex.center,
      justifyContent: props.onReset ? 'space-between' : 'flex-end',
      borderTop: '1px solid #E8E8E8',
      padding: '10px 12px',
    }),
    okbtn: {
      cursor: 'pointer',
      border: 'none',
      padding: '1px 8px',
      background: '#38B2A6',
      fontSize: '12px',
      lineHeight: '22px',
      color: theme.colors.gray1,
      borderRadius: '2px',
      ...theme.flex.center,
    },
    cancelBtn: {
      cursor: 'pointer',
      fontSize: '12px',
      lineHeight: '22px',
      border: 'none',
      marginRight: '12px',
      borderRadius: '2px',
      paddingLeft: '8px',
      paddingRight: '8px',
      '&:hover': {
        backgroundColor: theme.colors.gray3,
      },
    },
    disabledbtn: {
      color: theme.colors.disableColor,
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
    },
    inputContainer: {
      padding: '12px',
      position: 'relative',
      width: '100%',
    },
    searchInput: {
      border: '1px solid rgba(0,0,0,.15)',
      borderRadius: theme.borderRadius.min,
      color: '#4c4c4c',
      fontSize: '14px',
      height: '32px',
      lineHeight: '22px',
      margin: '0',
      outline: 0,
      padding: '5px 22px 5px 8px',
      width: '100%',
      appearance: 'none',
      '&::placeholder': {
        fontSize: '14px',
        color: 'rgba(0,0,0,.15)',
      },
      '&:focus': {
        borderColor: theme.colors.mainGreen6,
      },
    },
    searchIcon: {
      color: theme.colors.disableColor,
      position: 'absolute',
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'inline-block',
      fontStyle: 'normal',
      lineHeight: '0',
      textAlign: 'center',
      marginRight: '8px',
    },
    filterDropdownBox: {
      minWidth: '240px',
      paddingTop: '4px',
      '& .ant-checkbox-group': {
        width: '100%',
        maxHeight: 320,
        paddingTop: '4px',
        paddingBottom: '4px',
        overflowY: 'auto',
      },

      '& .ant-checkbox-wrapper': {
        display: 'block',
        margin: '0',
        paddingLeft: '12px',
        paddingTop: '5px',
        paddingBottom: '5px',
        fontSize: '14px',
        lineHeight: '22px',
        maxWidth: '240px',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        color: 'rgba(0, 0, 0, 0.85)',
        fontWeight: 400,
        '&:hover': {
          backgroundColor: theme.colors.gray3,
        },
      },
      '& .ant-checkbox-wrapper-checked': {
        backgroundColor: `${theme.colors.mainGreen1} !important`,
        color: theme.colors.mainGreen6,
      },
    },
    centerSpin: {
      display: 'flex',
      justifyContent: 'center',
    },
  }),
  { name: 'scrollLoadCheckBoxGroup' },
);
/**
 * Different with onChange and OnSelect
 * onChange is okBtn callback
 * onSelect is everyTime select option callback.
 * onSelect is design for notification parent component for storage selected origin data
 * to avoid new search cause list update and lost selected data.
 */

export function ScrollLoadCheckBoxGroup({
  list = [],
  visible,
  onClose,
  onReachEndList,
  onChange,
  onSelect,
  value,
  onSearch,
  searchPlaceholder,
  loading,
  onReset,
  top,
}) {
  const classes = useStyles({ onReset, top });
  const [searchValue, setSearchValue] = useState('');
  const [selected, setSelected] = useState(value);

  const requestSearch = useCallback(debounce(onSearch, 2000), []);

  const handleSearch = e => {
    setSearchValue(e.target.value);
    requestSearch(e.target.value);
  };

  const handleSelect = v => {
    setSelected(v);
    onSelect(v);
  };

  const handleConfirm = () => {
    onChange(selected);
  };

  const handleReset = () => {
    setSearchValue('');
    setSelected([]);
    onChange([]);
    onReset();
  };

  useEffect(() => {
    setSelected(value);
  }, [value]);

  return (
    <If condition={visible}>
      <div className={classes.filterTable}>
        <div className={classes.inputContainer}>
          <input
            onChange={handleSearch}
            placeholder={searchPlaceholder}
            className={classes.searchInput}
            value={searchValue}
          />
          <SearchOutlined className={classes.searchIcon} />
        </div>
        <Spin spinning={loading}>
          <div className={classes.filterDropdownBox}>
            <Checkbox.Group
              onScroll={e => {
                e.persist();
                const target = e.target;
                if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
                  if (!loading) {
                    onReachEndList && onReachEndList();
                  }
                }
              }}
              options={list}
              value={selected}
              onChange={handleSelect}
            />
          </div>
        </Spin>

        <div className={classes.bottomBox}>
          {onReset && (
            <button
              className={classNames(classes.cancelBtn, {
                [classes.disabledbtn]: selected.length === 0,
              })}
              disabled={selected.length === 0}
              onClick={() => {
                handleReset();
                onClose();
              }}
            >
              Reset
            </button>
          )}
          <button
            className={classes.okbtn}
            onClick={() => {
              handleConfirm();
              onClose();
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </If>
  );
}
